import { motion } from "framer-motion";
import {
  Anchor,
  ArrowUpRight,
  CircleDot,
  Dumbbell,
  Flag,
  Landmark,
  LifeBuoy,
  Music,
  Newspaper,
  Route,
  Sailboat,
  ShoppingBasket,
  Sunset,
  TreePine,
  Trophy,
} from "lucide-react";
import { EASE } from "@/lib/scroll";

const SCENES = [
  {
    icon: Trophy,
    title: "Award-Winning Tennis",
    desc: "Courts voted among the best in Manitoba, with attendants and programs July 1 to mid-August.",
    link: "https://www.victoriabeachclub.com/recreation-program",
    linkLabel: "victoriabeachclub.com",
  },
  {
    icon: LifeBuoy,
    title: "Swimming & Kids' Programs",
    desc: "Swimming club, lessons, movie nights and teen events. A full summer of recreation.",
    link: "https://www.victoriabeachclub.com/recreation-program",
    linkLabel: "victoriabeachclub.com",
  },
  {
    icon: Dumbbell,
    title: "Fitness Centre",
    desc: "A 1,000 sq ft fitness centre right in the community. Sign up for a 2 week membership or the Beaches “6 Pack”; 6 visits at your leisure.",
    link: "https://www.beachescc.ca/fitness-signup",
    linkLabel: "beachescc.ca",
  },
  {
    icon: ShoppingBasket,
    title: "Country Market & Breakfast",
    desc: "A short bike ride to the Saturday breakfast at the Community Centre, 9am to noon, then browse the weekly Country Market 10am to 1pm.",
    link: "https://www.beachescc.ca/beaches-country-market",
    linkLabel: "beachescc.ca",
  },
  {
    icon: Music,
    title: "Summer Winds Festival",
    desc: "The Summer Winds Music Festival at the Community Centre, a highlight of the season.",
    link: "https://www.summerwindsmusic.com/",
    linkLabel: "summerwindsmusic.com",
  },
  {
    icon: CircleDot,
    title: "Pickleball",
    desc: "Courts built three years ago and busy every morning. Bring a paddle.",
    link: "https://www.beachescc.ca/pickle-ball",
    linkLabel: "beachescc.ca",
  },
  {
    icon: Flag,
    title: "Golf",
    desc: "A 9-hole course within a bike ride; an 18-hole course fifteen minutes by car.",
    link: "https://victoriabeach.ca/p/golf-course",
    linkLabel: "victoriabeach.ca",
  },
  {
    icon: Route,
    title: "Groomed Trails",
    desc: "A comprehensive network of groomed hiking trails through the pines.",
    link: "https://victoriabeach.ca/p/trails",
    linkLabel: "victoriabeach.ca",
  },
  {
    icon: Anchor,
    title: "The Historic Pier",
    desc: "At one end of Victoria Beach, with beaches to explore in both directions.",
  },
  {
    icon: Newspaper,
    title: "The VB Herald",
    desc: "Published weekly since 1925. Grab a paper copy at newsstands around VB, or read online.",
    link: "https://www.victoriabeachclub.com/current-issue",
    linkLabel: "victoriabeachclub.com",
  },
  {
    icon: Landmark,
    title: "A Century of History",
    desc: "Victoria Beach has been a beloved summer colony for generations. Read the whole story.",
    link: "https://victoriabeach.ca/p/our-history",
    linkLabel: "victoriabeach.ca",
  },
  {
    icon: TreePine,
    title: "Elk Island Provincial Park",
    desc: "A 30 to 45 minute hike through the trails. Take the McCord Trail shortcut and keep an eye out for wildlife.",
    link: "https://www.beachesof59.ca/elk",
    linkLabel: "beachesof59.ca",
  },
  {
    icon: Sunset,
    title: "The Sunsets",
    desc: "Over Lake Winnipeg, the 11th largest lake in the world. Partly cloudy days are the best kind.",
  },
  {
    icon: Sailboat,
    title: "Victoria Beach Yacht Club",
    desc: "Sign up for sailing lessons and racing events, right on the lake.",
    link: "https://www.vbyc.ca/",
    linkLabel: "vbyc.ca",
  },
];

export default function SummerScene() {
  return (
    <section
      id="scene"
      data-testid="summer-scene-section"
      className="mx-auto max-w-7xl px-6 py-24 sm:px-10 sm:py-36"
    >
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: EASE }}
        className="mb-16 max-w-2xl"
      >
        <div className="mb-6 flex items-center gap-4">
          <span className="h-px w-12 bg-[#C8634B]" />
          <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#C8634B]">
            Local Life
          </p>
        </div>
        <h2 className="font-serif text-3xl leading-snug tracking-tight text-[#1E3323] sm:text-4xl lg:text-5xl">
          Six weeks of <span className="italic text-[#C8634B]">everything.</span>
        </h2>
        <p className="mt-5 max-w-md text-sm leading-relaxed text-[#4A4D46] sm:text-base">
          From July 1 to mid-August, Victoria Beach runs a full summer program, then the community returns to its perfect, lamp-less quiet.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {SCENES.map((item, i) => (
          <motion.div
            key={item.title}
            data-testid={`scene-card-${i + 1}`}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.6, delay: (i % 4) * 0.08, ease: EASE }}
            className="group flex flex-col rounded-xl border border-[#1E3323]/12 bg-white/60 p-6 transition-all duration-300 hover:-translate-y-1 hover:border-[#C8634B]/50 hover:bg-white hover:shadow-lg hover:shadow-[#1E3323]/5"
          >
            <span className="mb-4 block h-0.5 w-8 bg-[#C8634B] transition-all duration-300 group-hover:w-12" />
            <h3 className="font-sans text-base font-semibold tracking-wide text-[#1E3323]">
              {item.title}
            </h3>
            <p className="mt-1.5 flex-1 text-sm leading-relaxed text-[#767970]">
              {item.desc}
            </p>
            {item.link && (
              <a
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`scene-link-${i + 1}`}
                className="mt-4 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[#C8634B] transition-colors hover:text-[#1E3323]"
              >
                {item.linkLabel} <ArrowUpRight size={12} />
              </a>
            )}
          </motion.div>
        ))}
      </div>
    </section>
  );
}
